import React from "react"
import styled from "styled-components"
import { Link } from "gatsby"
import { useGetTrainingAndVideo } from "../hooks/useGetTrainingAndVideo"
import { useCreateAnalyticsEvent } from "../hooks/useCreateAnalyticsEvent"
import DescriptionItem from "../Component/DescriptionItem"
import Image from "../Component/Image"
import VideoSwiper from "../Component/VideoSwiper"
import { SampleTraining, VideoUrls } from "../Interface"
import { color, font } from "../Util/theme"
import Config from "../Config"

const { PageUrl, Icon, ImageFile, AnalyticsEvent } = Config

const ContentContainer = ({ location }) => {
  const { training, videoUrls } = useGetTrainingAndVideo({ location })
  const analyticsEvent = (label: string) => useCreateAnalyticsEvent(label)
  return (
    <ContentPage
      training={training}
      videoUrls={videoUrls}
      analyticsEvent={analyticsEvent}
    />
  )
}

interface ContentPageProps {
  training: SampleTraining
  videoUrls: VideoUrls
  analyticsEvent: (label: string) => () => void
}

const ContentPage: React.FC<ContentPageProps> = ({
  training,
  videoUrls,
  analyticsEvent,
}) => {
  return (
    <Root>
      <BgImage src={training.background_img} />
      <ScreenView>
        <Header>
          <BackButton>
            <Link
              to="/"
              style={{ textDecoration: "none" }}
              onClick={analyticsEvent(AnalyticsEvent.Back)}
            >
              <Image filename={Icon.Back} />
            </Link>
          </BackButton>
          <TitleTextContainer>
            <TitleText>{training.name}</TitleText>
          </TitleTextContainer>
        </Header>
        <TrainerContainer>
          <TrainingIconContainer>
            <TrainerIcon src={training.trainer_icon} />
            <TrainerInfo>
              <TrainerName>{training.trainer_name}</TrainerName>
            </TrainerInfo>
          </TrainingIconContainer>
        </TrainerContainer>
        <ExplanationContainer>
          <ExplanationText>{training.explain}</ExplanationText>
        </ExplanationContainer>
        {training.video_count && (
          <VideoContainer>
            <VideoSwiper videoUrls={videoUrls} isTopText height="275px" />
          </VideoContainer>
        )}
        <DescriptionContainer>
          <DescriptionTitleContainer>
            <DescriptionTitle> クラス詳細</DescriptionTitle>
          </DescriptionTitleContainer>
          <DescriptionItems>
            <DescriptionItem
              icon={Icon.Level}
              label={"レベル"}
              value={training.level_name}
            />
            <DescriptionItem
              icon={Icon.Time}
              label={"時間"}
              value={training.training_time + "分"}
            />
            <DescriptionItem
              icon={Icon.Note}
              label={"音楽ジャンル"}
              value={training.genre_name}
            />
            {training.calories && (
              <DescriptionItem
                icon={Icon.Burn}
                label={"消費カロリー"}
                value={training.calories + "kcal"}
              />
            )}
            {training.distance && (
              <DescriptionItem
                icon={Icon.Distance}
                label={"距離"}
                value={training.distance + " km"}
              />
            )}
            {training.max_speed && (
              <DescriptionItem
                icon={Icon.Speed}
                label={"最大速度"}
                value={training.max_speed + " km"}
              />
            )}
            {training.max_incline && (
              <DescriptionItem
                icon={Icon.Incline}
                label={"最大傾斜"}
                value={training.max_incline + " %"}
              />
            )}
          </DescriptionItems>
        </DescriptionContainer>
        <BlankViewContainer />
        <StartButtonContainer>
          <Link
            to={PageUrl.Sound}
            state={{ training, videoUrls }}
            style={{ textDecoration: "none" }}
            onClick={analyticsEvent(
              `${AnalyticsEvent.PlaySound}:${training.name}`
            )}
          >
            <StartButton>
              <Image filename={ImageFile.Start} />
            </StartButton>
          </Link>
        </StartButtonContainer>
      </ScreenView>
    </Root>
  )
}

const Root = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  padding-bottom: 90px;
`
const BgImage = styled.img`
  position: fixed;
  top: 0px;
  right: 0px;
  width: 100%;
  height: 110vh;
  object-fit: cover;
`
const Header = styled.div`
  position: sticky;
  top: 0px;
  height: 60px;
  background-color: ${color.white};
  padding-bottom: 15px;
  z-index: 2;
`
const BackButton = styled.div`
  position: sticky;
  top: 30px;
  left: 14px;
  width: 23.5px;
  height: 23px;
  z-index: 2;
`
const TitleTextContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  top: 28px;
  left: 0px;
  right: 0px;
`
const TitleText = styled.div`
  font-size: 17px;
  font-family: ${font.W6};
`
const ScreenView = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
`
const TrainerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 22px;
  margin-left: 15px;
  margin-right: 15px;
  margin-bottom: 15px;
  background-color: transparent;
`
const TrainingIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const TrainerIcon = styled.img`
  width: 53px;
  height: 53px;
  border-radius: 26.5px;
  margin-right: 8px;
  margin-top: 15px;
`
const TrainerInfo = styled.div`
  justify-content: center;
`
const TrainerName = styled.div`
  font-size: 18px;
  color: ${color.black};
  font-weight: 600;
  font-family: ${font.W3};
`
const ExplanationContainer = styled.div`
  margin-bottom: 21px;
  margin-right: 15px;
  margin-left: 15px;
  background-color: transparent;
`
const ExplanationText = styled.div`
  font-size: 16px;
  font-family: ${font.W3};
`
const VideoContainer = styled.div`
  margin-bottom: 20px;
  border-top-width: 0.5px;
  border-top-color: ${color.blueGray};
  border-bottom-width: 0.5px;
  border-bottom-color: ${color.blueGray};
  background-color: ${color.white};
`
const DescriptionContainer = styled.div`
  opacity: 0.8;
  margin-bottom: 20px;
  background-color: ${color.weekBlue};
  border-top-width: 0.5px;
  border-top-color: ${color.blueGray};
  border-bottom-width: 0.5px;
  border-bottom-color: ${color.blueGray};
`
const DescriptionTitleContainer = styled.div`
  padding-top: 16px;
  margin-top: 20px;
  margin-left: 16px;
  margin-right: 16px;
  margin-bottom: 32px;
`
const DescriptionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  font-family: ${font.W6};
`
const DescriptionItems = styled.div`
  margin-left: 32px;
  margin-right: 34px;
  margin-bottom: 16px;
  padding-bottom: 16px;
`
const BlankViewContainer = styled.div`
  height: 80px;
  background-color: transparent;
`
const StartButtonContainer = styled.div`
  display: flex;
  position: fixed;
  justify-content: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: ${color.white};
  z-index: 2;
  bottom: 0px;
  left: 0px;
  right: 0px;
`
const StartButton = styled.div`
  width: 360px;
  height: 65px;
`

export default ContentContainer
